import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { Messages } from '@dojo/framework/i18n/i18n';
import { Store } from '@dojo/framework/stores/Store';
import { I18nThemed } from '@holmescorp/interfaces/mixins';
import { MPEItems, lccstore, userstore } from '@holmescorp/lcc-state';
import { logQuiz } from '@holmescorp/logger';
import { mayTriggerML } from '@holmescorp/ml';
import { publish } from '@holmescorp/pub-sub';

import css from '../css/questionfeedback.m.css';
import {
	InfoRPC,
	ProgressionProperties,
	Question,
	QuestionQueueType,
	QuizMeta,
	QuizRPC,
	StartQuiz
} from '../interfaces';
import { ActiveQuizSection } from '../quizsection';
import { IAuditSvc, svc as auditsvc } from '../support/audit-svc';
import { IQuestionFeedback, svc } from '../support/qfsvc';
import { QuestionList } from '../support/questionlist';
import quizsvc from '../support/quizsvc';
import {
	infoChange,
	quizChange,
	setInreview,
	setIsaudit,
	setProgressionIsrunning,
	setQuestion,
	setTitle
} from '../support/state';
import { recordProgress, recordStart } from '../support/xapi';

export interface BaseProperties extends ProgressionProperties {
	quizstore: Store<QuizMeta>;
	quiz?: QuizRPC;
	info?: InfoRPC;
	quizurl?: string;
	isrunning?: string;
	instancekey?: string;
	stopLoading?: () => void;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };
	question?: Question;
	runningmpe?: MPEItems;
	activempe?: ActiveQuizSection;
}

@theme(css)
export class BaseProgression extends I18nThemed<BaseProperties> {
	protected startquiz: StartQuiz = { cursor: 0, status: 'question' };
	protected svc: IQuestionFeedback = svc;
	protected auditsvc: IAuditSvc = auditsvc;
	private _propsInited: boolean = false;
	private _lastIsrunning = '';
	protected _inAction = false;

	protected onAttach(): void {
		const { stopLoading, quizstore } = this.properties;
		stopLoading && stopLoading();
		setProgressionIsrunning(quizstore)({ isrunning: '' });
	}

	protected onDetach(): void {
		//
	}

	protected async load_quiz(): Promise<boolean> {
		const { quizurl = '', quizstore } = this.properties;
		const { svc } = this;
		svc._smd!.url = quizurl;

		const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);

		const { title } = quiz;
		setTitle(quizstore)({ title });
		quizChange(quizstore)({ quiz });
		infoChange(quizstore)({ info });
		setQuestion(quizstore)({ question: undefined });
		return true;
	}

	protected async quiz_startup(isRestart: boolean = false): Promise<boolean> {
		const { svc } = this;
		const { quizstore } = this.properties;

		const quiz = await svc.quiz();
		const info = await svc.info();

		setProgressionIsrunning(quizstore)({ isrunning: 'quiz' });
		quizChange(quizstore)({ quiz });
		infoChange(quizstore)({ info });
		setQuestion(quizstore)({ question: undefined });
		info.runningtime && !isRestart ? recordProgress(quiz, info) : recordStart(quiz, isRestart);
		this.invalidate();

		publish('quiz:status-change', { quizid: quiz.id });
		return true;
	}

	protected async finalize(): Promise<boolean> {
		const { quizstore } = this.properties;
		const { get, path } = userstore;
		const { svc } = this;
		const userid = get(path('user_id'));
		const { value: studyplan } = path('toolstate', 'studyplan');

		const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);

		setProgressionIsrunning(quizstore)({ isrunning: 'final' });

		quizChange(quizstore)({ quiz });
		infoChange(quizstore)({ info });
		setQuestion(quizstore)({ question: undefined });

		await quizsvc.update_annotation({ userid });
		this.invalidate();
		if (quiz.kind === 'pretest' && !studyplan.ml.inFlight) {
			mayTriggerML(true);
		}
		publish('quiz:status-change', { quizid: quiz.id, status: 'final' });

		return true;
	}

	protected render_loading(): DNode[] {
		const { key } = this.properties;
		return [
			v('div', { key: `spinner-${key}`, classes: [this.theme(css.loading)] }, [
				v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
			])
		];
	}

	/*
	 * audit support (primarily for q+f)
	 */
	protected audit_init(): void {
		const { quizurl = '' } = this.properties;
		const { auditsvc } = this;
		logQuiz.info(`run_audit quizurl = "${quizurl}"`);
		auditsvc._smd!.url = quizurl;
	}

	protected audit_startup(): Promise<boolean> {
		const {
			localizedBundle: {
				messages: { audit: auditText = '' }
			},
			quiz,
			quizstore
		} = this.properties;

		setProgressionIsrunning(quizstore)({ isrunning: 'audit' });
		setTitle(quizstore)({ title: `${quiz ? quiz.title : ''} ${auditText}` });
		setQuestion(quizstore)({ question: undefined });
		this.invalidate();

		return Promise.resolve(true);
	}

	protected run_audit(): DNode[] {
		const { quiz = {} as QuizRPC, info, quizstore } = this.properties;
		const { startquiz, auditsvc } = this;
		const resultid = info && info.lastresult.id;

		const finalize = () => {
			setIsaudit(quizstore)({ isaudit: false });
			setInreview(quizstore)({ inreview: false });
			setTitle(quizstore)({ title: quiz.title });
			setProgressionIsrunning(quizstore)({ isrunning: '' });
			this.invalidate();
		};
		return [w<QuestionQueueType>(QuestionList, { svc: auditsvc as any, finalize, startquiz, resultid, quizstore })];
	}

	/*
	 * add dynls quiz namespace bundle to properties
	 */
	@beforeProperties()
	protected ensure_nls(properties: BaseProperties & WidgetProperties): BaseProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls && dynls.get('quiz');
		properties.localizedBundle = bundle
			? this.localizeBundle(bundle)
			: { isPlaceholder: true, format: null, messages: {} as Messages };
		properties.quizid = properties.quiz ? properties.quiz.id : '';
		return properties;
	}

	private propsInit(paths: string[][], store: Store<QuizMeta>) {
		if (!this._propsInited) {
			const watch = (store: Store<any>, paths: string[][]) => {
				const invalidatePaths = paths.map((path: any) => store.path(path.join('/')));
				const handle = store.onChange(invalidatePaths, () => this.invalidate());
				this.own({
					destroy: () => {
						handle.remove();
					}
				});
			};

			watch(store, paths);
			watch(lccstore, [['dynls']]);

			this._propsInited = true;
		}
	}

	@beforeProperties()
	protected stateProperties(properties: BaseProperties & WidgetProperties): BaseProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		this.propsInit(
			[['quiz'], ['info'], ['quizurl'], ['progressionState', 'isrunning'], ['instancekey']],
			quizstore
		);

		properties.quiz = get(path('quiz'));
		properties.info = get(path('info'));
		properties.quizurl = get(path('quizurl'));
		properties.isrunning = get(path('progressionState', 'isrunning'));
		if (properties.isrunning !== this._lastIsrunning) {
			this._lastIsrunning = properties.isrunning || '';
			this._inAction = false;
		}
		properties.stopLoading = get(path('stopLoading'));
		properties.instancekey = get(path('instancekey'));
		properties.runningmpe = userstore.get(userstore.path('mpe'));
		properties.activempe = get(path('activeSection'));
		return properties;
	}
}
